import Vue from 'vue';
import VueRouter from 'vue-router';
import Home from '../views/Home.vue';
import Login from '../views/Login.vue';
import Signup from '../views/Signup.vue';
import Search from '../views/Search.vue';
import ProductDetail from '../views/ProductDetail.vue';
import Cart from '../views/Cart.vue';
import Seller from '../views/Seller.vue';
import AdminHome from '../views/AdminHome.vue';
import Addresses from '../views/Addresses.vue';
import Checkout from '../views/Checkout.vue';
import OrderSuccess from '../views/OrderSuccess.vue';
import Purchases from '../views/Purchases.vue';
import Profile from '../views/Profile.vue';
import ChangePassword from '../views/ChangePassword.vue';
import tokenService from '../services/tokenService';

import PrivacyPolicy from '../views/PrivacyPolicy.vue';
import RefundPolicy from '../views/RefundPolicy.vue';
import Terms from '../views/Terms.vue';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home
  },
  {
    path: '/admin/home',
    name: 'AdminHome',
    component: AdminHome,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/profile/requests',
    name: 'ProfileImageRequests',
    component: () => import('../views/ProfileImageRequests.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/responses/:id',
    name: 'ProfileImageResponse',
    component: () => import('../views/ProfileImageResponse.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/favourites',
    name: 'ProfileFavourites',
    component: () => import('../views/ProfileFavourites.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/admin/image-requests',
    name: 'AdminImageRequests',
    component: () => import('../views/AdminImageRequests.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  // T6b: the category/brand taxonomy editor. The older AdminCategories page (the
  // .xlsx upload for the legacy aesthetic collections) has been removed; this is
  // the only category admin surface now.
  {
    path: '/admin/category-tree',
    name: 'AdminCategoryTree',
    component: () => import('../views/AdminCategoryTree.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  // T7: the product editor. Distinct from AdminCategoryTree above, which edits
  // the taxonomy itself; this assigns products onto it.
  {
    path: '/admin/products',
    name: 'AdminProductEdit',
    component: () => import('../views/AdminProductEdit.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login
  },
  {
    path: '/signup',
    name: 'Signup',
    component: Signup
  },
  {
    path: '/search',
    name: 'Search',
    component: Search,
    props: route => ({ query: route.query.q || '' })
  },
  // The homepage "New Selection" See all target. Search cannot serve this: it
  // bails on an empty q, so /search?q= rendered a blank results page.
  {
    path: '/new-selection',
    name: 'NewSelection',
    component: () => import('../views/NewSelection.vue')
  },
  // T8: category and brand pages at any depth. The customer decides how many
  // levels the tree has, so one wildcard route handles /c/women,
  // /c/women/bags and /c/women/bags/gucci alike — the backend resolves whether
  // the last segment names a category or a brand.
  {
    path: '/c/*',
    name: 'CategoryListing',
    component: () => import('../views/CategoryListing.vue')
  },
  {
    path: '/product',
    name: 'ProductDetail',
    component: ProductDetail
  },
  {
    path: '/cart',
    name: 'Cart',
    component: Cart
  },
  {
    path: '/checkout',
    name: 'Checkout',
    component: Checkout,
    meta: { requiresAuth: true }
  },
  {
    path: '/order-success',
    name: 'OrderSuccess',
    component: OrderSuccess,
    meta: { requiresAuth: true }
  },
  {
    path: '/purchases',
    name: 'Purchases',
    component: Purchases,
    meta: { requiresAuth: true }
  },
  {
    path: '/seller',
    name: 'Seller',
    component: Seller
  },
  {
    path: '/addresses',
    name: 'Addresses',
    component: Addresses,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: { requiresAuth: true }
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: ChangePassword,
    meta: { requiresAuth: true }
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy
  },
  {
    path: '/refund-policy',
    name: 'RefundPolicy',
    component: RefundPolicy
  },
  {
    path: '/terms',
    name: 'Terms',
    component: Terms
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Always scroll to top when changing routes
    return { x: 0, y: 0 };
  }
});

// Track the previous route for error handling (no lock to avoid aborts)
let previousRoute = null;

// Simplified navigation guard - just handles automatic authentication
router.beforeEach(async (to, from, next) => {
  
  try {
    // Store current route as previous for error handling
    previousRoute = from;

    // If user navigates to login from any page, preserve where they came from
    // so that after logging in they stay on the same page.
    if (to.path === '/login' && !to.query?.redirect && from && from.path && from.path !== '/login') {
      next({ path: '/login', query: { redirect: from.fullPath || from.path } });
      return;
    }
    
    // Skip token check for public routes
    if (to.path === '/login' || to.path === '/signup' || to.path === '/' || to.path === '/search') {
      next();
      return;
    }

    // Check if route requires admin role
    // if (to.meta.requiresAdmin) {
    //   const userData = localStorage.getItem('user');
    //   if (!userData) {
    //     isNavigating = false;
    //     next('/');
    //     return;
    //   }

    //   const user = JSON.parse(userData);
    //   if (user.role !== 'admin') {
    //     isNavigating = false;
    //     next('/'); // Redirect non-admin users to home
    //     return;
    //   }
    // }

    if (to.meta.requiresAdmin) {
      try {
        const currentUser = await tokenService.getCurrentUser();
        if (!currentUser || currentUser.role !== 'admin') {
          next('/');
          return;
        }
      } catch (error) {
        next('/');
        return;
      }
    }

    // Check if user is authenticated via session
    if (!from.name) { // This indicates a page refresh
      try {
        const isAuthenticated = await tokenService.isUserAuthenticated();
        if (isAuthenticated) {
          next();
          return;
        }
      } catch (error) {
        console.error('Authentication check failed on page load:', error);
      }
    }

    // Check if route requires authentication
    if (to.meta.requiresAuth) {
      try {
        const isAuthenticated = await tokenService.isUserAuthenticated();
        if (!isAuthenticated) {
          next({ path: '/login', query: { redirect: to.fullPath } });
          return;
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        next({ path: '/login', query: { redirect: to.fullPath } });
        return;
      }
    }

    next();
  } catch (error) {
    console.error('Navigation error:', error);
    if (to.meta.requiresAuth) {
      next('/');
    } else {
      next();
    }
  }
});

// Handle errors during navigation
router.onError((error) => {
  console.error('Router error:', error);
  
  // Show notification
  if (window.Vue && window.Vue.prototype.$root) {
    window.Vue.prototype.$root.$emit('show-toast', {
      message: 'Navigation error: ' + (error.message || 'Please try again'),
      type: 'error'
    });
  }
  // Do not force navigation; let current navigation settle to avoid loops
});

export default router; 